/**
 * WHERE TO GO AFTER SIGN-IN. A deep link opened while signed out (a shared episode, /wallet from an email)
 * is remembered here and restored once the creator signs in, instead of every sign-in landing on the
 * dashboard. Session storage, not router state, because a Google redirect sign-in leaves the app and loses
 * any in-memory state.
 */
const KEY = 'wt_return_to';

/** Only an in-app path counts: a leading single slash, never a protocol-relative or absolute URL. */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && path !== '/';
}

export function rememberReturnPath(path: string): void {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(KEY, path);
  } catch {
    // Storage blocked (private window): the creator simply lands on the dashboard, as before.
  }
}

/** Reads the remembered path without consuming it — safe to call during render. */
export function peekReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem(KEY);
    return isSafeReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

/** Forgets the remembered path once it has been used, so it applies to one sign-in only. */
export function clearReturnPath(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing stored or storage blocked: nothing to clear.
  }
}
